import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

const RECENT_PRELOAD_ERROR_WINDOW_MS = 5_000;

const PRELOAD_ERROR_PATTERNS = [
	/Failed to fetch dynamically imported module/i,
	/error loading dynamically imported module/i,
	/Importing a module script failed/i,
	/Unable to preload CSS/i
];

let preloadErrorAt = 0;

/**
 * True when an error is a stale-build chunk failure: a lazy import whose
 * hashed chunk no longer matches the deployed build. Matches the browser
 * error messages directly, and treats any error raised shortly after a
 * 'vite:preloadError' event as one (message wording varies per browser).
 */
export const isPreloadError = (error?: unknown) => {
	if (preloadErrorAt && Date.now() - preloadErrorAt < RECENT_PRELOAD_ERROR_WINDOW_MS) {
		return true;
	}
	const message = error instanceof Error ? error.message : String(error ?? '');
	return PRELOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

/**
 * Vite emits 'vite:preloadError' when a dynamic import fails — the session is
 * on a stale build whose hashed chunk is gone (AP2-1821). The error is left
 * to surface so the ErrorBoundary renders the "new version available"
 * fallback; this handler only records the event for isPreloadError and reports
 * a New Relic page action (via the agent instance) instead of an error.
 * Deliberately no reload and no preventDefault: a swallowed preload error makes
 * React.lazy fail on an undefined module, which is a more confusing error than
 * the original.
 * https://vite.dev/guide/build.html#load-error-handling
 */
export const registerPreloadErrorHandler = (newRelicAgent?: BrowserAgent) => {
	const onPreloadError = () => {
		preloadErrorAt = Date.now();
		newRelicAgent?.addPageAction('vitePreloadError', { url: window.location.href });
	};

	window.addEventListener('vite:preloadError', onPreloadError);
	return () => window.removeEventListener('vite:preloadError', onPreloadError);
};
